import { fetchAirtableTable } from "../airtable/FetchAirtableTable";
import { AirtableRecordResponse, AirtableTableId } from "../airtable/types";
import { fetchAssetData } from "../FetchAssetData";
import { RailData } from "./types";

/**
 * Fetches and enriches a Collection rail with its associated asset data from both Airtable and ATOM
 *
 * @remarks
 * This function serves as a bridge between Airtable Collections (rails) and the production ATOM API.
 * It takes a Collection record from Airtable, retrieves all referenced assets, and enriches each
 * asset with full production data from ATOM. This is commonly used when building browse pages
 * where each rail (Collection) contains multiple content tiles (Assets).
 *
 * **Data Flow:**
 * 1. Extracts asset IDs from the Collection's "Assets" field
 * 2. Fetches corresponding asset records from Airtable using a filtered query
 * 3. Sorts assets to match the original order specified in the Collection
 * 4. Enriches each asset with ATOM production data in parallel
 * 5. Filters out any assets that failed to fetch
 *
 * **Important Behaviors:**
 * - Assets are sorted to preserve the order defined in the Collection's "Assets" field
 * - Individual asset fetch failures are caught, logged, and filtered out (non-blocking)
 * - All ATOM data fetches happen in parallel for optimal performance
 * - Throws an error if the "Assets" field is missing or undefined
 *
 * @param rail - AirtableRecordResponse for a Collection record from Airtable
 * @returns Promise that resolves to a RailData object containing:
 *   - `self` - The original Collection record from Airtable
 *   - `assets` - Array of AssetData objects with both Airtable and ATOM data
 *
 * @throws Error if the "Assets" field is undefined in the Collection record
 *
 * @example
 * ```typescript
 * // Fetch a Collection from Airtable
 * const collectionRecord = await fetchAirtableRecord(
 *   `${AirtableTableId.Collections}/recABC123`
 * );
 *
 * // Enrich it with asset data
 * const railData = await fetchRailData(collectionRecord);
 *
 * // Access the Collection metadata
 * console.log(railData.self.fields["Collection Name"]);
 *
 * // Render tiles from the enriched assets
 * railData.assets.forEach(asset => {
 *   console.log(asset.atom.attributes.name); // ATOM production data
 *   console.log(asset.airtable["Custom Field"]); // Airtable custom data
 * });
 * ```
 */

const fetchRailData = async (
  rail: AirtableRecordResponse,
): Promise<RailData> => {
  if ("Assets" in rail.fields && rail.fields.Assets !== undefined) {
    const railAssetIds = rail.fields.Assets;
    const filter = encodeURIComponent(`FIND(RECORD_ID(), "${railAssetIds}")`);

    // Get back only referenced assets
    const { records } = await fetchAirtableTable(
      `${AirtableTableId.Assets}?filterByFormula=${filter}`,
    );

    // sort records into correct order
    records.sort(
      (a, b) => railAssetIds.indexOf(a.id) - railAssetIds.indexOf(b.id),
    );

    // get ATOM data and return array of AssetData objects
    const assetPromises = records.map(async (record) => {
      try {
        return await fetchAssetData(record);
      } catch (error) {
        console.error(`Failed to fetch asset ${record.id}:`, error);
        return null;
      }
    });

    const assetResults = await Promise.all(assetPromises);
    const assets = assetResults.filter((asset) => asset !== null);

    return { self: rail, assets };
  } else {
    throw new Error(`'Assets' is undefined for the collection ${rail.id}`);
  }
};

export { fetchRailData };
